// Customer.jsx
import { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  where,
  query,
  updateDoc,
  doc,
  Timestamp,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { toast, ToastContainer } from 'react-toastify';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import { CustomerCreationModal } from './CustomerCreationModal';
import CustomerVehiclesModal from './CustomerVehiclesModal';
import EditCustomerModal from './EditCustomerModal';
import { generateCustomerNumber } from './generateCustomerNumber';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import BreadCrumb from '../BreadCrumb';
import logger from '../../utils/logger';
import SearchBar from './SearchBar';
import CustomerTable from './CustomerTable';
import RecoveryPanel from './RecoveryPanel';
import {
  UserPlusIcon,
  ArrowDownTrayIcon,
  LifebuoyIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

const pages = [
  { name: 'Customer Care', to: '/customers', component: Link, current: false },
  { name: 'Customer Admin', to: '/customers/manage', component: Link, current: true },
];

const initialCustomerFormState = {
  customerNumber: '',
  firstName: '',
  lastName: '',
  email: '',
  address: '',
  phoneNumber: '',
  dateOfBirth: '',
  customerType: '',
};

export const Customer = () => {
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  // Removed selectedCustomer and showModal from state
  const [isVehiclesModalOpen, setIsVehiclesModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(initialCustomerFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Removed isSuccess from state
  const [isReferred, setIsReferred] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showRecoveryPanel, setShowRecoveryPanel] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const customersPerPage = 10;
  const pagesVisited = pageNumber * customersPerPage;

  const initialFormState = {
    firstName: '',
    lastName: '',
    customerType: '',
    dateOfBirth: '',
    gender: '',
    phoneNumber: '',
    email: '',
    address: '',
    agentId: '',
    specialNotes: '',
    preferences: [],
  };
  const [formState, setFormState] = useState(initialFormState);

  useEffect(() => {
    const fetchAgents = async () => {
      const agentSnapshot = await getDocs(collection(db, 'agents'));
      const agentList = agentSnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setAgents(agentList);
    };

    fetchAgents();
  }, []);

  const fetchCustomers = async (loadMore = false) => {
    setIsLoading(true);

    try {
      const customersCollection = collection(db, 'customers');

      // Pagination: Fetch only 50 customers at a time
      const PAGE_SIZE = 50;
      let customersQuery = query(
        customersCollection,
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );

      // If loading more, start after the last document
      if (loadMore && lastVisible) {
        customersQuery = query(
          customersCollection,
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(PAGE_SIZE)
        );
      }

      const customerSnapshot = await getDocs(customersQuery);

      if (customerSnapshot.docs.length > 0) {
        setLastVisible(customerSnapshot.docs[customerSnapshot.docs.length - 1]);
        setHasMore(customerSnapshot.docs.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }

      const customerList = customerSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // If loading more, append; otherwise replace
      if (loadMore) {
        setCustomers(prevCustomers => [...prevCustomers, ...customerList]);
      } else {
        setCustomers(customerList);
        setLastVisible(null);
      }
    } catch (error) {
      logger.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedCustomers = [...customers].sort((a, b) => {
    if (sortConfig.key) {
      const order = sortConfig.direction === 'asc' ? 1 : -1;
      if (
        typeof a[sortConfig.key] === 'string' &&
        typeof b[sortConfig.key] === 'string'
      ) {
        return a[sortConfig.key].localeCompare(b[sortConfig.key]) * order;
      } else {
        return (a[sortConfig.key] < b[sortConfig.key] ? -1 : 1) * order;
      }
    }
    return 0;
  });

  // Filter customers based on searchTerm
  const filteredCustomers = sortedCustomers.filter((customer) =>
    `${customer.firstName} ${customer.lastName}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleInputChange = (e) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Query to find customers created today to ensure sequential numbering
    const todayQuery = query(
      collection(db, 'customers'),
      where('createdAt', '>=', Timestamp.fromDate(today)),
      where('createdAt', '<', Timestamp.fromDate(tomorrow))
    );

    try {
      const todaySnapshot = await getDocs(todayQuery);
      const customerCountToday = todaySnapshot.size + 1; // Add one for the new customer

      let customerData = {
        ...formState,
        editedAt: Timestamp.now(),
      };

      if (!formState.id) {
        const newCustomerNumber = generateCustomerNumber(
          formState.firstName,
          formState.lastName,
          formState.customerType, // 'I' for individual, 'C' for corporate
          customerCountToday
        );

        customerData = {
          ...customerData,
          customerNumber: newCustomerNumber,
          createdAt: Timestamp.now(),
        };

        delete customerData.id;
        const docRef = await addDoc(collection(db, 'customers'), customerData);
        setCustomers((prevCustomers) => [
          ...prevCustomers,
          { id: docRef.id, ...customerData },
        ]);
        toast.success('Customer created successfully!');
      } else {
        // Update existing customer logic here...
      }

      setFormState(initialFormState);
      setIsModalOpen(false); // Use setIsModalOpen instead of setShowModal
    } catch (error) {
      logger.error('Error in customer operation:', error);
      toast.error('An error occurred while processing the customer.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCustomer && editingCustomer.id) {
        // Update existing customer
        const updatedCustomerData = {
          ...editingCustomer,
          editedAt: Timestamp.now(),
        };
        const customerRef = doc(db, 'customers', editingCustomer.id);
        await updateDoc(customerRef, updatedCustomerData);
        toast.success('Customer updated successfully!');

        // Update the customer in the local state
        setCustomers((prevCustomers) =>
          prevCustomers.map((customer) =>
            customer.id === editingCustomer.id ? updatedCustomerData : customer
          )
        );
      }

      setIsEditModalOpen(false);
    } catch (error) {
      logger.error('Error updating customer:', error);
      toast.error('An error occurred while updating the customer.');
    }
  };

  const deleteCustomer = async (customerId) => {
    if (!customerId) {
      toast.error('Invalid customer ID.');
      return;
    }

    try {
      await deleteDoc(doc(db, 'customers', customerId));
      toast.success('Customer deleted successfully!');
      setCustomers((prevCustomers) =>
        prevCustomers.filter((customer) => customer.id !== customerId)
      );
    } catch (error) {
      logger.error('Error deleting customer:', error);
      toast.error('Failed to delete customer.');
    }
  };

  const openEditModal = (customer) => {
    setEditingCustomer({ ...customer });
    setIsEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    setEditingCustomer((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditModalOpen(false);
    setIsEditing(false);
    setFormState(initialFormState);
  };

  const handleCloseModal = () => {
    setIsVehiclesModalOpen(false);
  };

  const fetchCustomerVehicles = async (customerId) => {
    try {
      const vehiclesRef = collection(db, 'vehicles');
      const q = query(vehiclesRef, where('customerId', '==', customerId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        logger.info('No matching vehicles found');
        return [];
      }

      let customerVehicles = [];
      querySnapshot.forEach((doc) => {
        customerVehicles.push({ id: doc.id, ...doc.data() });
      });

      return customerVehicles;
    } catch (error) {
      logger.error('Error fetching vehicles: ', error);
      throw error;
    }
  };

  const handleVehiclesButtonClick = async (customer) => {
    setIsLoading(true);
    const vehicles = await fetchCustomerVehicles(customer.id);
    setCustomerVehicles(vehicles);
    setIsLoading(false);
    setIsVehiclesModalOpen(true);
  };

  const changePage = ({ selected }) => {
    setPageNumber(selected);
  };

  const exportToCSV = () => {
    const csvData = customers.map((customer) => ({
      CustNo: customer?.customerNumber,
      Name: customer.firstName + ' ' + customer.lastName,
      Email: customer.email,
      PhoneNumber: customer.phoneNumber,
      DOB: customer.dateOfBirth,
      Type: customer.customerType,
      Gender: customer.gender,
      Address: customer.address,
    }));

    const csvString = Papa.unparse(csvData, {
      header: true,
    });

    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'Customers.csv');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <BreadCrumb pages={pages} />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ToastContainer position="top-right" autoClose={3000} />

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                <UsersIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                  Customer Management
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage and organize your customer database
                </p>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="hidden lg:flex items-center space-x-6 bg-white rounded-xl px-6 py-4 shadow-md border border-gray-100">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{customers.length}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
              </div>
              <div className="h-10 w-px bg-gray-200"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{filteredCustomers.length}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Filtered</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Modals */}
          <CustomerCreationModal
            showModal={isModalOpen}
            closeModal={closeModal}
            handleSubmit={handleSubmit}
            formState={formState}
            handleInputChange={handleInputChange}
            isEditing={isEditing}
            agents={agents}
            isReferred={isReferred}
            setIsReferred={setIsReferred}
            setFormState={setFormState}
          />

          {/* Search and Action Bar */}
          <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200 px-6 py-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Search Bar - Enhanced */}
              <div className="flex-1 max-w-md">
                <SearchBar setSearchTerm={setSearchTerm} />
              </div>

              {/* Action Buttons - Enhanced */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="group relative inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium text-sm rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  <UserPlusIcon className="h-5 w-5" />
                  <span>Create Customer</span>
                </button>

                <button
                  onClick={exportToCSV}
                  className="group relative inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-700 text-white font-medium text-sm rounded-xl shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 hover:from-green-700 hover:to-emerald-800 transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  <span>Export CSV</span>
                </button>

                <button
                  onClick={() => setShowRecoveryPanel(!showRecoveryPanel)}
                  className={`group relative inline-flex items-center gap-2 px-5 py-2.5 font-medium text-sm rounded-xl shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 ${
                    showRecoveryPanel
                      ? 'bg-gradient-to-r from-red-600 to-orange-700 text-white shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40'
                      : 'bg-gradient-to-r from-orange-600 to-amber-700 text-white shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40'
                  }`}
                >
                  <LifebuoyIcon className="h-5 w-5" />
                  <span>{showRecoveryPanel ? 'Hide Recovery' : 'Recovery Panel'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Recovery Panel - Enhanced with Animation */}
          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              showRecoveryPanel ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="p-6 bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 border-b border-orange-200">
              <RecoveryPanel />
            </div>
          </div>

          {/* Table Container */}
          <div className="overflow-x-auto">
            <CustomerTable
              customers={filteredCustomers}
              pagesVisited={pagesVisited}
              customersPerPage={customersPerPage}
              handleSort={handleSort}
              sortConfig={sortConfig}
              isLoading={isLoading}
              handleVehiclesButtonClick={handleVehiclesButtonClick}
              openEditModal={openEditModal}
              deleteCustomer={deleteCustomer}
            />
          </div>

          {/* Pagination - Enhanced */}
          {filteredCustomers.length > 0 && (
            <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-t border-gray-200 px-6 py-5">
              <ReactPaginate
                previousLabel={
                  <span className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </span>
                }
                nextLabel={
                  <span className="flex items-center gap-1.5">
                    Next
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                }
                pageCount={Math.ceil(filteredCustomers.length / customersPerPage)}
                onPageChange={changePage}
                containerClassName="flex items-center justify-center gap-2"
                previousLinkClassName="px-4 py-2 bg-white text-blue-600 font-semibold text-sm rounded-lg border border-gray-300 hover:bg-blue-50 hover:border-blue-400 transition-all duration-200 shadow-sm hover:shadow-md"
                nextLinkClassName="px-4 py-2 bg-white text-blue-600 font-semibold text-sm rounded-lg border border-gray-300 hover:bg-blue-50 hover:border-blue-400 transition-all duration-200 shadow-sm hover:shadow-md"
                pageClassName="hidden sm:block"
                pageLinkClassName="px-3.5 py-2 bg-white text-gray-700 font-medium text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                breakClassName="px-2 py-1"
                breakLinkClassName="text-gray-500"
                activeClassName="ring-2 ring-blue-500"
                activeLinkClassName="!bg-gradient-to-r !from-blue-600 !to-indigo-600 !text-white !border-transparent font-bold shadow-lg"
              />
            </div>
          )}

          {/* Load More Button */}
          {hasMore && !isLoading && customers.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-center">
              <button
                onClick={() => fetchCustomers(true)}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Load More Customers
              </button>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        <EditCustomerModal
          showModal={isEditModalOpen}
          closeModal={() => setIsEditModalOpen(false)}
          handleSubmit={handleEditSubmit}
          formState={editingCustomer}
          handleInputChange={handleEditInputChange}
          agents={agents}
        />

        {/* Vehicles Modal */}
        {isVehiclesModalOpen && customerVehicles.length > 0 && (
          <CustomerVehiclesModal
            vehicles={customerVehicles}
            onClose={handleCloseModal}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};
